import { useTenant } from "@/stores/tenantStore.tsx";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/Button.tsx";
import { useAuthStore } from "@/stores/authStore.ts";
import { authApi } from "@/api/auth.ts";
import { SEOHead } from "@/components/seo/SEOHead.tsx";

type Mode = "email" | "code";

export function OtpPage() {
  const { siteName } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuthStore();

  const state = location.state as { email?: string; verify?: boolean } | null;
  const isVerifyOnly = !!state?.verify;

  const [mode, setMode] = useState<Mode>(state?.email ? "code" : "email");
  const [email, setEmail] = useState(state?.email ?? "");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  const handleSendOtp = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await authApi.sendOtp(email);
      setMode("code");
    } catch (err) {
      setError((err as Error).message ?? "Failed to send code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (isVerifyOnly) {
        await authApi.verifyEmail(email, code);
        navigate("/dashboard");
      } else {
        const data = await authApi.verifyOtp(email, code);
        setAuth({ user: data.user, token: data.token, expiresAt: data.expiresAt });
        navigate("/dashboard");
      }
    } catch (err) {
      setError((err as Error).message ?? "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResent(false);
    try {
      if (isVerifyOnly) {
        await authApi.resendVerification();
      } else {
        await authApi.sendOtp(email);
      }
      setResent(true);
    } catch {
      setError("Failed to resend code");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: "#faf8f3" }}>
      <div className="w-full max-w-[400px]">
      <SEOHead title="Verify" description="Enter your verification code." path="/otp" noindex={true} />
      <div className="mb-8 text-center">
        <span className="text-2xl font-bold text-surface-900">{siteName}</span>
      </div>

      {mode === "email" ? (
        <>
          <h1 className="text-2xl font-semibold text-surface-900 mb-1">Sign in with email</h1>
          <p className="text-[13px] text-surface-400 mb-6">We'll send a 6-digit code to your email</p>

          <form onSubmit={handleSendOtp} className="space-y-4">
            <div>
              <label className="block text-[12px] font-medium text-surface-700 mb-1.5">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" autoComplete="email" required
                className="w-full h-10 px-3 rounded-lg border border-surface-200 bg-white text-[13px] text-surface-900
                           placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
              />
            </div>
            {error && <p className="text-[12px] text-danger">{error}</p>}
            <Button type="submit" loading={loading} fullWidth>Send code</Button>
          </form>

          <p className="mt-6 text-center text-[13px] text-surface-400">
            <Link to="/login" className="text-brand-600 hover:underline font-medium">Back to login</Link>
          </p>
        </>
      ) : (
        <>
          <h1 className="text-2xl font-semibold text-surface-900 mb-1">
            {isVerifyOnly ? "Verify your email" : "Enter your code"}
          </h1>
          <p className="text-[13px] text-surface-400 mb-6">
            We sent a 6-digit code to <strong className="text-surface-700">{email}</strong>
          </p>

          <form onSubmit={handleVerifyCode} className="space-y-4">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              placeholder="000000"
              autoFocus
              className="w-full px-4 py-4 text-center text-2xl tracking-[0.5em] font-mono rounded-lg border border-surface-200
                         focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none"
            />
            {error && <p className="text-[12px] text-danger">{error}</p>}
            <Button type="submit" loading={loading} disabled={code.length !== 6} fullWidth>
              {isVerifyOnly ? "Verify email" : "Sign in"}
            </Button>
          </form>

          <p className="mt-4 text-center text-[13px] text-surface-400">
            Didn't get a code?{" "}
            <button onClick={handleResend} className="text-brand-600 hover:underline font-medium">
              Resend
            </button>
            {resent && <span className="ml-2 text-success">Sent!</span>}
          </p>

          <p className="mt-2 text-center text-[13px] text-surface-400">
            <Link to="/login" className="text-surface-400 hover:text-surface-600 hover:underline">Back to login</Link>
          </p>
        </>
      )}
      </div>
    </div>
  );
}
